import { useEffect, useState } from "react";
import {
  isRouteErrorResponse,
  useLocation,
  useRouteError,
} from "react-router-dom";

import {
  attemptChunkReload,
  isChunkLoadError,
  isChunkReloadPending,
} from "@/lib/chunkReload";
import { captureError } from "@/lib/sentry";

import { ErrorPage, type ErrorPageDetail } from "./ErrorPage";
import { errorKindFromStatus, type ErrorKind } from "./errorCatalog";

/**
 * Route ``errorElement`` — replaces React Router's built-in
 * "Unexpected Application Error!" stack-trace screen.
 *
 * This is the only place a lazy route's failed ``import()`` can be
 * caught. React Router owns an error boundary INSIDE ``<RouterProvider>``
 * and catches the rejection before it can reach the app-level
 * ``<ErrorBoundary>`` wrapped around the provider, and ``React.lazy``
 * handles the promise itself so no ``unhandledrejection`` fires either.
 * Without an ``errorElement`` here, both of those recovery paths are
 * unreachable for exactly the failure they were written for.
 */

function resolveKind(error: unknown): ErrorKind {
  // ``isChunkReloadPending`` covers the case where the ``vite:preloadError``
  // listener already called ``preventDefault()``: Vite then resolves the
  // import to ``undefined`` instead of rethrowing, so React.lazy throws
  // "Cannot read properties of undefined (reading 'default')" — a message
  // ``isChunkLoadError`` can't recognise, on a page that is already
  // reloading.
  if (isChunkLoadError(error) || isChunkReloadPending()) return "stale_version";
  if (isRouteErrorResponse(error)) return errorKindFromStatus(error.status);
  return "unknown";
}

function messageOf(error: unknown): string | undefined {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  return typeof error === "string" ? error : undefined;
}

export function RouteErrorPage(): JSX.Element {
  const error = useRouteError();
  const { pathname } = useLocation();
  const kind = resolveKind(error);
  const [eventId, setEventId] = useState<string>();

  useEffect(() => {
    if (kind !== "stale_version") return;
    // Defence in depth: if the window listener never fired, this is
    // the last chance to recover the tab without the user acting.
    attemptChunkReload();
  }, [kind]);

  useEffect(() => {
    // A stale chunk is expected after a deploy, not a defect — keep it
    // out of Sentry so real errors stay visible.
    if (kind === "stale_version") return;
    let alive = true;
    void captureError(error).then((id) => {
      if (alive) setEventId(id);
    });
    return () => {
      alive = false;
    };
  }, [error, kind]);

  const detail: ErrorPageDetail = {
    message: messageOf(error),
    route: pathname,
    stack: error instanceof Error ? error.stack : undefined,
    eventId,
  };

  return <ErrorPage kind={kind} detail={detail} />;
}
